import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, Group, Stack, Text, ThemeIcon, UnstyledButton } from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronRight,
  IconClipboardCheck,
  IconClockExclamation,
  IconCoins,
  IconSparkles,
} from '@tabler/icons-react';
import type { AttentionItem, AttentionKind } from '@/lib/dashboard';
import { ROUTES } from '@/routes/paths';
import classes from './homeRow.module.css';

const VIEW: Readonly<Record<AttentionKind, { color: string; icon: typeof IconCoins }>> = {
  blocked: { color: 'red', icon: IconAlertTriangle },
  overdue: { color: 'red.7', icon: IconClockExclamation },
  settlement: { color: 'wheat.7', icon: IconCoins },
  action: { color: 'brand', icon: IconClipboardCheck },
  info: { color: 'lime.7', icon: IconSparkles },
};

interface AttentionListProps {
  readonly items: readonly AttentionItem[];
}

// Лента приоритетных дел. Каждое — ссылка на карточку сделки. Заголовок строки: для приостановок —
// готовый текст из уведомления; для «моего хода» — «Сделка №X: <действие>».
export default function AttentionList({ items }: AttentionListProps) {
  const { t } = useTranslation();

  return (
    <Card withBorder padding={0}>
      <Stack gap={0}>
        {items.map((item) => {
          const view = VIEW[item.kind];
          const Icon = view.icon;
          const dealLabel = t('app.home.deal', { no: item.dealNo });
          // Заголовок: приостановки — готовый текст; просрочка — «<мой ход> · просрочено» (или «срок истёк»,
          // если хода нет); прочее — «Сделка №X: <действие>».
          let headline: string;
          if (item.title) {
            headline = item.title;
          } else if (item.kind === 'overdue') {
            headline = item.actionLabelKey
              ? `${dealLabel}: ${t(item.actionLabelKey)} · ${t('app.home.overdueSuffix')}`
              : `${dealLabel}: ${t('app.home.action.overdue')}`;
          } else {
            headline = `${dealLabel}: ${item.actionLabelKey ? t(item.actionLabelKey) : ''}`;
          }
          return (
            <UnstyledButton
              key={item.key}
              component={Link}
              to={`${ROUTES.APP_DEALS}/${item.dealId}`}
              className={classes.row}
            >
              <Group align="flex-start" wrap="nowrap" gap="sm">
                <ThemeIcon variant="light" color={view.color} size="md" radius="xl">
                  <Icon size={16} />
                </ThemeIcon>
                <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
                  <Text fw={600} size="sm">
                    {headline}
                  </Text>
                  {item.goods ? (
                    <Text size="xs" c="gray.6" truncate>
                      {item.goods}
                    </Text>
                  ) : null}
                </Stack>
                <IconChevronRight size={16} color="var(--mantine-color-gray-5)" style={{ flexShrink: 0, alignSelf: 'center' }} />
              </Group>
            </UnstyledButton>
          );
        })}
      </Stack>
    </Card>
  );
}
